import { Button, Modal, type DomSerialized, JsonToHtml } from '@imprivata-cloud/components';
import React, { useState, useCallback, type UIEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import './DataProcessingAddendumPage.less';
import { DpaStatus, StorageKeys } from '../../../api/constants';
import QuestionSvg from '../../../assets/icons/question.svg?react';
import { STEPS } from '../../constants';
import { getConsentDocument } from '../../../api/setupServices';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { AppError, type ApiError } from '../../../errorHandler/errors';

const TRANSLATION_ROOT = 'setup.data-processing-addendum.content';

// Note the format!
export const DEFAULT_DPA_DOCUMENT_NAME = 'impr-dpa-consent-v4_0.html';

const DataProcessingAddendumPage = () => {
  const { t } = useTranslation();
  const navigateToNextStep = useNavigateToNextStep();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [consentDocument, setConsentDocument] = useState<DomSerialized>();

  const onSubmit = () => {
    sessionStorage.setItem(StorageKeys.DPA_STATUS, DpaStatus.GRANTED);
    navigateToNextStep(STEPS.ORG_INFO);
  };

  const onDecline = () => {
    navigateToNextStep(STEPS.START);
  };

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      const dpaDocUrl = sessionStorage.getItem(StorageKeys.DPA_DOCUMENT_URL);
      if (!dpaDocUrl) {
        throw AppError.fromMessage('Invalid state exception: empty DPA doc URL.');
      }
      const resp = await getConsentDocument(dpaDocUrl.replace('.html', '.json'));
      setConsentDocument(resp);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  }, [emitError, clearErrorNotifications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const [visibleYouMust, setVisibleYouMust] = useState(false);
  const openYouMust = () => setVisibleYouMust(true);
  const closeYouMust = () => setVisibleYouMust(false);

  const [bottomReached, setBottomReached] = useState(false);

  const handleScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const target: HTMLDivElement = e.target as HTMLDivElement;
    if (
      target.scrollHeight - target.scrollTop <=
      target.clientHeight + 1 // +1 to compensate fractions
    ) {
      setBottomReached(true);
    }
  }, []);

  return !consentDocument ? (
    <></>
  ) : (
    <>
      <div className="dpa-text-container" onScroll={handleScroll} data-testid="dpa-container">
        <JsonToHtml json={consentDocument} />
      </div>
      <div className="dpa-footer">
        <Button
          label={t(`${TRANSLATION_ROOT}.agree`)}
          disabled={!bottomReached}
          type="primary"
          data-testid="dpa-agree-btn"
          onClick={onSubmit}
        />
        <Button label={t(`${TRANSLATION_ROOT}.decline`)} data-testid="dpa-decline-btn" onClick={openYouMust} />
      </div>
      <Modal
        open={visibleYouMust}
        destroyOnClose
        closable={false}
        className="modal-dpa-you-must"
        width={600}
        centered={true}
        onCancel={closeYouMust}
        // cannot use standard footer since primary and secondary button actions
        // are swapped for this form
        footer={(_) => (
          <>
            <Button
              label={t(`${TRANSLATION_ROOT}.go-back`)}
              type="primary"
              data-testid="dpa-you-must-modal-agree-btn"
              onClick={closeYouMust}
            />
            <Button
              label={t(`${TRANSLATION_ROOT}.decline`)}
              data-testid="dpa-you-must-modal-decline-btn"
              onClick={onDecline}
            />
          </>
        )}
      >
        <QuestionSvg style={{ width: '4rem', margin: '.5rem' }} />
        <h2>{t(`${TRANSLATION_ROOT}.decline-modal-title`)}</h2>
        <p>{t(`${TRANSLATION_ROOT}.decline-modal-text`)}</p>
      </Modal>
    </>
  );
};

export default DataProcessingAddendumPage;
